import React from "react";
import { useAuth } from "../../hooks";

/**
 * RoleGuard Component
 * Conditionally renders children based on user permissions and roles
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to render when authorized
 * @param {string[]} props.permissions - Required permissions
 * @param {string[]} props.roles - Required roles
 * @param {boolean} props.requireAll - Whether all permissions/roles are required
 * @param {React.ReactNode} props.fallback - Content to render when not authorized
 * @param {boolean} props.showFallback - Whether to show fallback or nothing
 * @param {Function} props.onAccessDenied - Callback when access is denied
 * @param {boolean} props.invertCheck - Invert the permission check (show when NOT authorized)
 */
export const RoleGuard = ({
  children,
  permissions = [],
  roles = [],
  requireAll = false,
  fallback = null,
  showFallback = true,
  onAccessDenied,
  invertCheck = false,
}) => {
  const { isAuthenticated, user, rbac } = useAuth();

  // Not authenticated - deny access (unless invertCheck is true)
  if (!isAuthenticated) {
    const shouldShow = invertCheck;
    if (shouldShow) {
      return children;
    }
    if (onAccessDenied) {
      onAccessDenied("not_authenticated", null, null);
    }
    return showFallback ? fallback : null;
  }

  // Check role-based access
  let hasRoleAccess = true;
  if (roles && roles.length > 0) {
    hasRoleAccess = roles.includes(user?.role);
  }

  // Check permission-based access
  let hasPermissionAccess = true;
  if (permissions && permissions.length > 0) {
    hasPermissionAccess = requireAll
      ? rbac.hasAllPermissions(permissions)
      : rbac.hasAnyPermission(permissions);
  }

  // Combine role and permission checks
  const hasAccess = hasRoleAccess && hasPermissionAccess;
  const shouldShow = invertCheck ? !hasAccess : hasAccess;

  if (shouldShow) {
    return children;
  }

  // Access denied
  if (onAccessDenied) {
    onAccessDenied(
      hasRoleAccess ? "insufficient_permissions" : "insufficient_role",
      permissions,
      roles
    );
  }

  return showFallback ? fallback : null;
};

/**
 * Permission Guard - specifically for permission-based access
 */
export const PermissionGuard = ({
  children,
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
}) => {
  const permissionList = permission ? [permission] : permissions;

  return (
    <RoleGuard
      permissions={permissionList}
      requireAll={requireAll}
      fallback={fallback}
    >
      {children}
    </RoleGuard>
  );
};

/**
 * Role-specific Guard
 */
export const RoleSpecificGuard = ({
  children,
  role,
  roles = [],
  fallback = null,
}) => {
  const roleList = role ? [role] : roles;

  return (
    <RoleGuard roles={roleList} fallback={fallback}>
      {children}
    </RoleGuard>
  );
};

/**
 * Admin Guard - shows content only to admins
 */
export const AdminGuard = ({ children, fallback = null }) => {
  return (
    <RoleSpecificGuard roles={["SUPER_ADMIN", "ADMIN"]} fallback={fallback}>
      {children}
    </RoleSpecificGuard>
  );
};

/**
 * User Guard - shows content only to regular users (not admins)
 */
export const UserGuard = ({ children, fallback = null }) => {
  return (
    <RoleSpecificGuard
      roles={["USER", "MANAGER", "TEAM_LEAD"]}
      fallback={fallback}
    >
      {children}
    </RoleSpecificGuard>
  );
};

/**
 * Owner Guard - shows content only to resource owner or admins
 */
export const OwnerGuard = ({
  children,
  resourceUserId,
  allowAdmins = true,
  fallback = null,
}) => {
  const { user } = useAuth();

  const isOwner = user?.id === resourceUserId;
  const isAdmin = allowAdmins && ["SUPER_ADMIN", "ADMIN"].includes(user?.role);

  if (isOwner || isAdmin) {
    return children;
  }

  return fallback;
};

/**
 * Development Guard - shows content only in development mode
 */
export const DevGuard = ({ children, fallback = null }) => {
  const isDev = process.env.NODE_ENV === "development";

  if (isDev) {
    return children;
  }

  return fallback;
};

/**
 * Feature Flag Guard - shows content based on feature flags
 */
export const FeatureGuard = ({
  children,
  feature,
  enabled = true,
  fallback = null,
}) => {
  // You can integrate with your feature flag system here
  const isFeatureEnabled =
    process.env[`REACT_APP_FEATURE_${feature?.toUpperCase()}`] === "true";

  if (isFeatureEnabled === enabled) {
    return children;
  }

  return fallback;
};

/**
 * Combined Guard - supports multiple guard types
 */
export const CombinedGuard = ({
  children,
  permissions = [],
  roles = [],
  requireAll = false,
  checkOwnership = false,
  resourceUserId = null,
  allowAdmins = true,
  feature = null,
  invertCheck = false,
  fallback = null,
}) => {
  return (
    <RoleGuard
      permissions={permissions}
      roles={roles}
      requireAll={requireAll}
      invertCheck={invertCheck}
      fallback={
        <OwnerGuard
          resourceUserId={checkOwnership ? resourceUserId : null}
          allowAdmins={allowAdmins}
          fallback={
            <FeatureGuard feature={feature} fallback={fallback}>
              {children}
            </FeatureGuard>
          }
        >
          {children}
        </OwnerGuard>
      }
    >
      {children}
    </RoleGuard>
  );
};

/**
 * Conditional Guard - shows different content based on permissions
 */
export const ConditionalGuard = ({
  permissions = [],
  roles = [],
  requireAll = false,
  whenAuthorized,
  whenUnauthorized = null,
  whenNotAuthenticated = null,
}) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated && whenNotAuthenticated) {
    return whenNotAuthenticated;
  }

  return (
    <RoleGuard
      permissions={permissions}
      roles={roles}
      requireAll={requireAll}
      fallback={whenUnauthorized}
    >
      {whenAuthorized}
    </RoleGuard>
  );
};

/**
 * Hook for checking permissions in components
 */
export const usePermissions = () => {
  const { rbac, user, isAuthenticated } = useAuth();

  return {
    hasPermission: (permission) => rbac.hasPermission(permission),
    hasAnyPermission: (permissions) => rbac.hasAnyPermission(permissions),
    hasAllPermissions: (permissions) => rbac.hasAllPermissions(permissions),
    hasRole: (role) => user?.role === role,
    hasAnyRole: (roles) => roles.includes(user?.role),
    isAdmin: () => ["SUPER_ADMIN", "ADMIN"].includes(user?.role),
    isUser: () => ["USER", "MANAGER", "TEAM_LEAD"].includes(user?.role),
    canAccess: (permissions = [], roles = [], requireAll = false) => {
      if (!isAuthenticated) return false;

      let hasRoleAccess = true;
      if (roles.length > 0) {
        hasRoleAccess = roles.includes(user?.role);
      }

      let hasPermissionAccess = true;
      if (permissions.length > 0) {
        hasPermissionAccess = requireAll
          ? rbac.hasAllPermissions(permissions)
          : rbac.hasAnyPermission(permissions);
      }

      return hasRoleAccess && hasPermissionAccess;
    },
  };
};

/**
 * Higher-order component for wrapping components with role guards
 */
export const withRoleGuard = (WrappedComponent, guardConfig = {}) => {
  const GuardedComponent = (props) => {
    return (
      <RoleGuard {...guardConfig}>
        <WrappedComponent {...props} />
      </RoleGuard>
    );
  };

  GuardedComponent.displayName = `withRoleGuard(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return GuardedComponent;
};

/**
 * Render prop component for permission-based rendering
 */
export const PermissionProvider = ({
  children,
  permissions = [],
  roles = [],
  requireAll = false,
}) => {
  const { isAuthenticated, user, rbac } = useAuth();

  const hasRoleAccess = roles.length === 0 || roles.includes(user?.role);
  const hasPermissionAccess =
    permissions.length === 0 ||
    (requireAll
      ? rbac.hasAllPermissions(permissions)
      : rbac.hasAnyPermission(permissions));

  const hasAccess = isAuthenticated && hasRoleAccess && hasPermissionAccess;

  return typeof children === "function"
    ? children({
        hasAccess,
        isAuthenticated,
        user,
        permissions: rbac.getUserPermissions(),
      })
    : hasAccess
    ? children
    : null;
};

// Export default
export default RoleGuard;
